import {
  OnboardingTarget,
  onboardingTarget,
} from "../onboarding/onboardingTargets";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import classNames from "classnames";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEnvironmentInteractionPoints,
  TakePosition,
} from "../../app/slices/EnvironmentSlice";
import {
  SelectedTab,
  requestViewportMapFocus,
  selectSelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectActors,
  selectSelectedElementId,
  selectSelectedTake3D,
  setSelectedInteraction,
  setSelectedTake,
  setSelectedTakeSequence,
} from "../../app/slices/TrainingSlice";
import { selectErrors, selectWarnings } from "../../app/slices/ValidationSlice";
import ActorAvatar from "../../components/actors/ActorAvatar";
import { useHelper3D } from "../../hooks/HelperHooks3D";
import usePropHelper from "../../hooks/PropHelper";
import Take3D, {
  getTake3DSequence,
  isTake3DTransitionEnabled,
} from "../../models/Take3D";
import { normalizeTransition } from "../../models/interactions/TransitionDefaults";
import ActorPresetsColumn from "../actor_presets/ActorPresetsColumn";
import PropPresetsColumn from "../prop_presets/PropPresetsColumn";
import { useAddTake3D } from "../timeline_3d/useAddTake3D";
import {
  EditorColumnHeader,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import ScenarioBuilderInteractionList from "./ScenarioBuilderInteractionList";

function getCurrentLocation(take: Take3D | undefined, positions: TakePosition[]) {
  if (!take?.interactionPointId) return undefined;

  return positions.find((position) => position.id === take.interactionPointId);
}

function getCurrentCameraName(take: Take3D, location?: TakePosition) {
  if (!take.mainCamera.cameraId) return "Default camera";

  const cameraId = take.mainCamera.cameraId.split("/").pop();
  return (
    location?.camera_positions.find((camera) => camera.id === cameraId)?.name ??
    take.mainCamera.cameraId
  );
}

function IssueBadge(props: { warningCount?: number; errorCount?: number }) {
  const { warningCount = 0, errorCount = 0 } = props;

  if (errorCount > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold flex-shrink-0 text-creator-error bg-creator-error-washed">
        <ErrorOutlineOutlinedIcon fontSize="inherit" />
        {errorCount}
      </span>
    );
  }

  if (warningCount > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold flex-shrink-0 text-[var(--pi-key-yellow)] bg-[var(--pi-key-yellow-faded)]">
        <WarningAmberOutlinedIcon fontSize="inherit" />
        {warningCount}
      </span>
    );
  }

  return null;
}

function OutlineRow(props: {
  icon: JSX.Element;
  iconClassName?: string;
  title: string;
  subtitle?: string;
  selected?: boolean;
  warningCount?: number;
  errorCount?: number;
  disabled?: boolean;
  borderless?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  actionIcon?: JSX.Element;
  onActionClick?: () => void;
  actionTestId?: string;
  testId?: string;
}) {
  const {
    icon,
    iconClassName,
    title,
    subtitle,
    selected,
    warningCount = 0,
    errorCount = 0,
    disabled,
    borderless,
    onClick,
    onDoubleClick,
    actionIcon,
    onActionClick,
    actionTestId,
    testId,
  } = props;

  return (
    <button
      type="button"
      className={classNames(
        "w-full min-w-0 flex items-center gap-2 rounded-5 text-left cursor-pointer box-border transition-colors border-[1.75px] border-solid",
        borderless ? "px-2 py-1.5" : "px-3 py-2 bg-creator-surface",
        selected
          ? "border-creator-primary bg-creator-primary-washed"
          : borderless
          ? "border-transparent hover:bg-creator-childHover"
          : "border-creator-border hover:bg-creator-childHover",
        disabled && "cursor-not-allowed opacity-55"
      )}
      onClick={disabled ? undefined : onClick}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      disabled={disabled}
      data-testid={testId}
      data-selected={selected ? "true" : undefined}
    >
      <span
        className={classNames(
          "inline-flex items-center justify-center flex-shrink-0 text-[18px]",
          selected ? "text-creator-primary" : "text-creator-primary",
          iconClassName
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 flex flex-col">
        <span
          className={classNames(
            "truncate text-[13px] font-medium leading-5",
            selected ? "text-creator-primary" : "text-creator-text"
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span className="truncate text-[11px] font-normal text-creator-text-sub leading-4">
            {subtitle}
          </span>
        )}
      </span>
      <span className="min-w-0 inline-flex items-center justify-end gap-1 flex-shrink-0">
        <IssueBadge warningCount={warningCount} errorCount={errorCount} />
        {actionIcon && (
          <span
            role="button"
            aria-label="Focus on map"
            title="Focus on map"
            className="inline-flex items-center justify-center rounded-full p-1 text-[16px] text-creator-text-sub hover:text-creator-primary hover:bg-creator-primary-washed cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              onActionClick?.();
            }}
            data-testid={actionTestId}
          >
            {actionIcon}
          </span>
        )}
      </span>
    </button>
  );
}

function ScenarioBuilderScene() {
  const dispatch = useDispatch();
  const take = useSelector(selectSelectedTake3D);
  const selectedElementId = useSelector(selectSelectedElementId);
  const actors = useSelector(selectActors);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const warnings = useSelector(selectWarnings);
  const errors = useSelector(selectErrors);
  const { addTake } = useAddTake3D();
  const { getInvolvedProps } = useHelper3D();
  const { getPropPresetByInstanceId } = usePropHelper();

  const currentLocation = useMemo(
    () => getCurrentLocation(take, takePositions),
    [take, takePositions]
  );
  const involvedProps = useMemo(
    () => (take ? getInvolvedProps(take) : []),
    [getInvolvedProps, take]
  );
  const focusLocationOnMap = () => {
    if (!currentLocation) return;
    dispatch(requestViewportMapFocus(currentLocation.position));
  };
  if (!take) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-5 text-center text-[32px] text-creator-text-sub">
        <MovieCreationOutlinedIcon fontSize="inherit" />
        <span className="text-[15px] font-normal">No take selected</span>
        <button
          type="button"
          className="h-[38px] inline-flex items-center justify-center gap-1.5 rounded-full px-4 bg-creator-primary hover:bg-creator-primary-hover text-white text-[14px] font-semibold cursor-pointer"
          onClick={addTake}
        >
          <AddIcon fontSize="inherit" />
          Add take
        </button>
      </div>
    );
  }

  const sequence = getTake3DSequence(take);
  const sequenceWarnings = sequence ? warnings[sequence.id]?.length ?? 0 : 0;
  const sequenceErrors = sequence ? errors[sequence.id]?.length ?? 0 : 0;
  const transition = normalizeTransition(take.transition);
  const hasTransition = isTake3DTransitionEnabled(take);
  const transitionWarnings = hasTransition
    ? warnings[take.transition?.id ?? ""]?.length ?? 0
    : 0;
  const transitionErrors = hasTransition
    ? errors[take.transition?.id ?? ""]?.length ?? 0
    : 0;

  return (
    <div className="h-full min-h-0 flex flex-col">
      <EditorColumnHeader
        icon={<MovieCreationOutlinedIcon fontSize="inherit" />}
        title={take.name}
        testId="scenario-builder-header"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2.5 space-y-1.5 pit-creator-scrollbar">
        <EditorColumnSection
          title="Take"
          icon={<MovieCreationOutlinedIcon fontSize="inherit" />}
          defaultExpanded
        >
          <OutlineRow
            icon={<LocationOnOutlinedIcon fontSize="inherit" />}
            title="Location"
            subtitle={currentLocation?.name ?? "Unassigned"}
            selected={selectedElementId === take.id}
            borderless
            onClick={() => dispatch(setSelectedTake(take.id))}
            onDoubleClick={focusLocationOnMap}
            actionIcon={
              currentLocation ? (
                <CenterFocusStrongOutlinedIcon fontSize="inherit" />
              ) : undefined
            }
            onActionClick={focusLocationOnMap}
            actionTestId="scenario-row-location-focus"
            testId="scenario-row-location"
          />
          <OutlineRow
            icon={<CameraAltOutlinedIcon fontSize="inherit" />}
            title="Camera"
            subtitle={getCurrentCameraName(take, currentLocation)}
            selected={selectedElementId === take.mainCamera.id}
            warningCount={warnings[take.mainCamera.id]?.length ?? 0}
            errorCount={errors[take.mainCamera.id]?.length ?? 0}
            borderless
            onClick={() => dispatch(setSelectedInteraction(take.mainCamera.id))}
            testId="scenario-row-camera"
          />
          <OutlineRow
            icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
            title={sequence?.name ?? "Sequence"}
            subtitle="OnTakeStart · OnTakeEnd"
            warningCount={sequenceWarnings}
            errorCount={sequenceErrors}
            selected={selectedElementId === sequence?.id}
            disabled={!sequence}
            borderless
            onClick={() => dispatch(setSelectedTakeSequence(take.id))}
            testId="scenario-row-sequence"
          />
          <OutlineRow
            icon={<SwapHorizOutlinedIcon fontSize="inherit" />}
            title="Transition"
            subtitle={
              hasTransition
                ? `${transition.name} · ${transition.duration}s`
                : "Disabled"
            }
            selected={selectedElementId === take.transition?.id}
            warningCount={transitionWarnings}
            errorCount={transitionErrors}
            borderless
            onClick={() => dispatch(setSelectedInteraction(take.transition.id))}
            testId="scenario-row-transition"
          />
        </EditorColumnSection>

        <EditorColumnSection
          title="Actors"
          icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
          defaultExpanded
        >
          {take.actors.length > 0 ? (
            take.actors.map((actorState) => {
              const actor = actors.find(
                (candidate) => candidate.id === actorState.actorId
              );
              const isSelected = selectedElementId === actorState.id;

              return (
                <button
                  key={actorState.id}
                  type="button"
                  className={classNames(
                    "w-full min-w-0 flex items-center gap-2 rounded-5 text-left cursor-pointer box-border",
                    "px-2 py-1.5 border-[1.75px] border-solid transition-colors",
                    isSelected
                      ? "border-creator-primary bg-creator-primary-washed"
                      : "border-transparent hover:bg-creator-childHover"
                  )}
                  onClick={() =>
                    dispatch(setSelectedInteraction(actorState.id))
                  }
                  data-testid={`scenario-row-actor-${actorState.id}`}
                  data-selected={isSelected ? "true" : undefined}
                >
                  <ActorAvatar
                    actor={actor}
                    size="24"
                    theme={isSelected ? "creator-selected" : "creator"}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={classNames(
                        "truncate block text-[13px] font-medium leading-5",
                        isSelected
                          ? "text-creator-primary"
                          : "text-creator-text"
                      )}
                    >
                      {actor?.workingName ?? actorState.actorId}
                    </span>
                  </span>
                  <span className="min-w-0 inline-flex items-center justify-end gap-1 flex-shrink-0">
                    <IssueBadge
                      warningCount={warnings[actorState.id]?.length ?? 0}
                      errorCount={errors[actorState.id]?.length ?? 0}
                    />
                  </span>
                </button>
              );
            })
          ) : (
            <div className="px-2 py-2 text-[13px] font-normal italic text-creator-text-sub">
              No actors in this take
            </div>
          )}
        </EditorColumnSection>

        <EditorColumnSection
          title="Props"
          icon={<Inventory2OutlinedIcon fontSize="inherit" />}
        >
          {involvedProps.length > 0 ? (
            involvedProps.map((propState) => {
              const propPreset = getPropPresetByInstanceId(propState.instanceId);

              return (
                <OutlineRow
                  key={propState.id}
                  icon={<Inventory2OutlinedIcon fontSize="inherit" />}
                  title={propPreset?.name ?? propState.instanceId}
                  subtitle={propState.positionId}
                  borderless
                  onClick={() => dispatch(setSelectedTake(take.id))}
                  testId={`scenario-row-prop-${propState.id}`}
                />
              );
            })
          ) : (
            <div className="px-2 py-2 text-[13px] font-normal italic text-creator-text-sub">
              No props in this take
            </div>
          )}
        </EditorColumnSection>

        <EditorColumnSection
          title="Interactions"
          icon={<TouchAppOutlinedIcon fontSize="inherit" />}
          defaultExpanded
        >
          <ScenarioBuilderInteractionList take={take} />
        </EditorColumnSection>
      </div>
    </div>
  );
}

export default function ScenarioBuilderColumn() {
  const selectedTab = useSelector(selectSelectedTab);

  return (
    <div
      className="w-full min-w-0 h-full flex flex-col bg-creator-surface border-r border-creator-border text-creator-text"
      data-testid="scenario-builder-column"
      {...onboardingTarget(OnboardingTarget.Editor3DScenarioBuilder)}
    >
      <div className="min-h-0 flex-1 overflow-hidden">
        {selectedTab === SelectedTab.Timeline && <ScenarioBuilderScene />}
        {selectedTab === SelectedTab.ActorPresets && <ActorPresetsColumn />}
        {selectedTab === SelectedTab.PropPresets && <PropPresetsColumn />}
      </div>
    </div>
  );
}
